(function () {
  // =====================================================================
  // Seal Subscriptions widget — custom layout shim
  // ---------------------------------------------------------------------
  // The Seal app renders its own widget DOM (.sealsubs-container). We reshape
  // it into the approved design (see seal-interval-shim.css):
  //   - two cards side by side: one-time | subscription
  //   - each shows price + "value per dose" (variant price / doses metafield)
  //   - subscription card shows a "Poupa X%" badge
  //   - the interval <select> is mirrored into the buy row as a pill dropdown,
  //     shown only when the subscription option is selected
  //
  // Seal's original markup inside each card is hidden by CSS; we still read and
  // click Seal's radio/select programmatically (works while hidden), so the
  // layout does not depend on Seal's exact inner structure.
  // =====================================================================

  var TRANSLATIONS = {
    en: {
      oneTimeName: "One-time purchase",
      subscriptionName: "Subscription",
      saveBadgePrefix: "Save",
      perDoseSuffix: "/dose",
      units: { day: ["day", "days"], week: ["week", "weeks"], month: ["month", "months"] }
    },
    pt: {
      oneTimeName: "Compra única",
      subscriptionName: "Subscrição",
      saveBadgePrefix: "Poupa",
      perDoseSuffix: "/dose",
      units: { day: ["dia", "dias"], week: ["semana", "semanas"], month: ["mês", "meses"] }
    },
    es: {
      oneTimeName: "Compra única",
      subscriptionName: "Suscripción",
      saveBadgePrefix: "Ahorra",
      perDoseSuffix: "/dosis",
      units: { day: ["día", "días"], week: ["semana", "semanas"], month: ["mes", "meses"] }
    }
  };

  function getLocaleKey() {
    var raw = (
      document.documentElement.lang ||
      (window.Shopify && window.Shopify.locale) ||
      "en"
    ).toLowerCase();
    var prefix = raw.split("-")[0];
    return TRANSLATIONS[prefix] ? prefix : "en";
  }

  function t(key) {
    var locale = getLocaleKey();
    var value = TRANSLATIONS[locale][key];
    return value !== undefined ? value : TRANSLATIONS.en[key];
  }

  // "2 weeks" -> "2 semanas" (with singular/plural). Leaves unknown patterns
  // untouched.
  function translateInterval(text) {
    var match = text.match(/(\d+)\s+(weeks?|days?|months?)\b/i);
    if (!match) return text;
    var n = parseInt(match[1], 10);
    var baseUnit = match[2].toLowerCase().replace(/s$/, "");
    var locale = getLocaleKey();
    var units = (TRANSLATIONS[locale] && TRANSLATIONS[locale].units) || TRANSLATIONS.en.units;
    var forms = units[baseUnit] || TRANSLATIONS.en.units[baseUnit];
    if (!forms) return text;
    return n + " " + (n === 1 ? forms[0] : forms[1]);
  }

  // ---- Selling-plan data (injected as JSON in the section) --------------
  var plansData = null;
  function getPlansData() {
    if (plansData !== null) return plansData;
    var el = document.getElementById("seal-shim-plans-data");
    if (!el) return (plansData = []);
    try {
      plansData = JSON.parse(el.textContent);
    } catch (e) {
      plansData = [];
    }
    return plansData;
  }

  function getAdjustmentForPlan(planId) {
    var groups = getPlansData();
    for (var i = 0; i < groups.length; i++) {
      var plans = groups[i].selling_plans || [];
      for (var j = 0; j < plans.length; j++) {
        if (String(plans[j].id) === String(planId)) {
          return (plans[j].price_adjustments || [])[0] || null;
        }
      }
    }
    return null;
  }

  // Whole-number discount percentage for a plan, or null.
  function getDiscountPct(planId) {
    var adj = getAdjustmentForPlan(planId);
    if (adj && adj.value_type === "percentage" && isFinite(adj.value)) {
      return adj.value;
    }
    return null;
  }

  // ---- Money helpers ---------------------------------------------------
  // Price template + base amount. We prefer the theme-controlled
  // `.seal-shim-price` element (it re-renders on variant change via
  // xo-product-will-change), falling back to Seal's own one-time price. Using
  // Seal's price alone is unreliable because the Seal app block does not
  // re-render on this theme's variant swap. We clone the .money node so the
  // currency symbol / separators match, then swap in the computed number.
  function moneyTemplate(container) {
    return (
      document.querySelector(".seal-shim-price .money") ||
      container.querySelector(".sls-one-time-price .money")
    );
  }

  function parseMoney(el) {
    if (!el) return null;
    var raw = el.textContent.replace(/\s+/g, "");
    var m = raw.match(/(\d+(?:[.,]\d+)?)/);
    return m ? parseFloat(m[1].replace(",", ".")) : null;
  }

  function baseAmount(container) {
    return parseMoney(moneyTemplate(container));
  }

  function formatMoney(templateEl, amount) {
    if (!templateEl || !isFinite(amount)) return "";
    var raw = templateEl.textContent.replace(/\s+/g, "");
    var numMatch = raw.match(/(\d+(?:[.,]\d+)?)/);
    if (!numMatch) return "";
    var baseStr = numMatch[1];
    var decimals = (baseStr.split(/[.,]/)[1] || "").length || 2;
    var useComma = baseStr.indexOf(",") !== -1;
    var formatted = amount.toFixed(decimals).replace(".", useComma ? "," : ".");
    var clone = templateEl.cloneNode(true);
    clone.textContent = raw.replace(/[\d.,]+/, formatted);
    return clone.outerHTML;
  }

  // Price for a subscription plan, computed from one-time base × adjustment.
  function computeAmount(planId, base) {
    if (!isFinite(base)) return null;
    var adj = getAdjustmentForPlan(planId);
    if (!adj) return null;
    if (adj.value_type === "percentage" && isFinite(adj.value)) {
      return base * (1 - adj.value / 100);
    }
    if (adj.value_type === "price" && isFinite(adj.value)) {
      return adj.value;
    }
    if (adj.value_type === "fixed_amount" && isFinite(adj.value)) {
      return base - adj.value;
    }
    return null;
  }

  // ---- Doses (value per dose) ------------------------------------------
  // Rendered by the section as <xo-product-will-change class="seal-shim-doses">
  // holding the selected variant's doses metafield; it re-renders on variant
  // change. Empty -> no per-dose line.
  function getDoses() {
    var el = document.querySelector(".seal-shim-doses");
    if (!el) return null;
    var n = parseFloat((el.textContent || "").trim().replace(",", "."));
    return isFinite(n) && n > 0 ? n : null;
  }

  function perDoseHTML(container, amount) {
    var doses = getDoses();
    var tmpl = moneyTemplate(container);
    if (!doses || !tmpl || !isFinite(amount)) return "";
    return (
      '<span class="seal-per-dose">' +
      formatMoney(tmpl, amount / doses) +
      t("perDoseSuffix") +
      "</span>"
    );
  }

  // ---- Cards -----------------------------------------------------------
  function oneTimeCard(container) {
    return (
      container.querySelector(".sls-option-container[data-one-time-option-radio]") ||
      container.querySelector(".sls-option-container:not([data-selling-plan-group])")
    );
  }

  function subCard(container) {
    return container.querySelector(".sls-option-container[data-selling-plan-group]");
  }

  function sealRadio(card) {
    return card ? card.querySelector('[role="radio"]') : null;
  }

  function isChecked(card) {
    var r = sealRadio(card);
    return !!(r && r.getAttribute("aria-checked") === "true");
  }

  // Build (once) the header row we control, and make the whole card select its
  // Seal option when clicked. Returns the row element.
  function ensureRow(card, kind) {
    var row = card.querySelector(".seal-card-row");
    if (!row) {
      row = document.createElement("div");
      row.className = "seal-card-row";

      var radio = document.createElement("span");
      radio.className = "seal-radio";

      var name = document.createElement("span");
      name.className = "seal-card-name";

      var spacer = document.createElement("span");
      spacer.className = "seal-card-spacer";

      var price = document.createElement("span");
      price.className = "seal-price-wrap";

      row.appendChild(radio);
      row.appendChild(name);
      if (kind === "sub") {
        var badge = document.createElement("span");
        badge.className = "seal-badge";
        badge.hidden = true;
        row.appendChild(badge);
      }
      row.appendChild(spacer);
      row.appendChild(price);

      card.insertBefore(row, card.firstChild);
    }

    // Keep the name in sync with the active locale (guarded: writing the same
    // textContent still replaces the text node, which would retrigger the
    // MutationObserver and loop).
    var nameEl = row.querySelector(".seal-card-name");
    var nameText = kind === "sub" ? t("subscriptionName") : t("oneTimeName");
    if (nameEl && nameEl.textContent !== nameText) nameEl.textContent = nameText;

    // Whole-card click selects this Seal option (once)
    if (card.dataset.shimClick !== "1") {
      card.dataset.shimClick = "1";
      card.addEventListener("click", function (e) {
        // let the mirrored dropdown handle its own clicks
        if (e.target.closest(".seal-interval-dropdown")) return;
        var r = sealRadio(card);
        if (r && r.getAttribute("aria-checked") !== "true") r.click();
      });
    }
    return row;
  }

  function setPrice(row, priceHTML, perDose) {
    var wrap = row.querySelector(".seal-price-wrap");
    if (!wrap) return;
    var html = (priceHTML ? '<span class="seal-price">' + priceHTML + "</span>" : "") + (perDose || "");
    if (wrap.innerHTML !== html) wrap.innerHTML = html;
  }

  function setBadge(row, pct) {
    var badge = row.querySelector(".seal-badge");
    if (!badge) return;
    if (pct && pct > 0) {
      var text = t("saveBadgePrefix") + " " + pct + "%";
      if (badge.textContent !== text) badge.textContent = text;
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }

  // ---- Interval dropdown (mirrored into the buy row) -------------------
  function buyForm() {
    return document.querySelector(".xo-product-info-content__buy-form");
  }

  function ensureDropdown(container) {
    var sub = subCard(container);
    var sealSelect = sub && sub.querySelector('select[name="subs_interval"]');
    var form = buyForm();
    if (!sealSelect || !form) return;

    var wrap = form.querySelector(".seal-interval-dropdown");
    var mirror;
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "seal-interval-dropdown";
      wrap.hidden = true;

      mirror = document.createElement("select");
      mirror.className = "seal-interval-select";
      mirror.setAttribute("aria-label", t("subscriptionName"));

      var chevron = document.createElement("span");
      chevron.className = "seal-chevron";
      chevron.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      wrap.appendChild(mirror);
      wrap.appendChild(chevron);
      form.insertBefore(wrap, form.firstChild);

      // Selecting an interval selects the subscription option and updates Seal
      mirror.addEventListener("change", function () {
        var r = sealRadio(sub);
        if (r && r.getAttribute("aria-checked") !== "true") r.click();
        sealSelect.value = mirror.value;
        sealSelect.dispatchEvent(new Event("change", { bubbles: true }));
        // reflect the new price immediately
        init(container);
      });
    } else {
      mirror = wrap.querySelector("select");
    }

    // Keep options in sync with Seal's select (translated labels)
    var desired = Array.prototype.map
      .call(sealSelect.options, function (o) {
        return o.value + "" + translateInterval(o.textContent.trim());
      })
      .join("");
    if (mirror.dataset.sig !== desired) {
      mirror.dataset.sig = desired;
      mirror.innerHTML = "";
      Array.prototype.forEach.call(sealSelect.options, function (o) {
        var opt = document.createElement("option");
        opt.value = o.value;
        opt.textContent = translateInterval(o.textContent.trim());
        mirror.appendChild(opt);
      });
    }
    if (mirror.value !== sealSelect.value) mirror.value = sealSelect.value;

    return sealSelect;
  }

  // ---- Selection state (highlight + dropdown visibility) --------------
  function updateSelection(container) {
    var ot = oneTimeCard(container);
    var sub = subCard(container);
    if (ot) ot.classList.toggle("seal-selected", isChecked(ot));
    if (sub) sub.classList.toggle("seal-selected", isChecked(sub));

    var wrap = buyForm() && buyForm().querySelector(".seal-interval-dropdown");
    if (wrap) wrap.hidden = !(sub && isChecked(sub));
  }

  // Observe aria-checked on both radios so highlight/visibility react instantly
  function watchRadios(container) {
    [oneTimeCard(container), subCard(container)].forEach(function (card) {
      var r = sealRadio(card);
      if (!r || r.dataset.shimWatched === "1") return;
      r.dataset.shimWatched = "1";
      new MutationObserver(function () {
        updateSelection(container);
      }).observe(r, { attributes: true, attributeFilter: ["aria-checked"] });
    });
  }

  function init(container) {
    var base = baseAmount(container);

    var ot = oneTimeCard(container);
    if (ot) {
      var otRow = ensureRow(ot, "onetime");
      setPrice(otRow, formatMoney(moneyTemplate(container), base), perDoseHTML(container, base));
    }

    var sub = subCard(container);
    if (sub) {
      var subRow = ensureRow(sub, "sub");
      var sealSelect = sub.querySelector('select[name="subs_interval"]');
      var planId = sealSelect ? sealSelect.value : null;
      var amount = planId ? computeAmount(planId, base) : null;
      setPrice(
        subRow,
        amount != null ? formatMoney(moneyTemplate(container), amount) : "",
        amount != null ? perDoseHTML(container, amount) : ""
      );
      setBadge(subRow, planId ? getDiscountPct(planId) : null);
      ensureDropdown(container);
    }

    watchRadios(container);
    updateSelection(container);
  }

  var observer = null;
  function startObserving() {
    if (observer) observer.observe(document.body, { childList: true, subtree: true });
  }

  function rescan() {
    // Pause observation while we mutate, so the shim's own DOM writes can never
    // retrigger it (which would loop). External changes after we reconnect
    // (Seal re-render, variant swap) still fire a fresh rescan.
    if (observer) observer.disconnect();
    try {
      document.querySelectorAll(".sealsubs-container").forEach(init);
    } finally {
      startObserving();
    }
  }

  function boot() {
    observer = new MutationObserver(function () {
      rescan();
    });
    rescan();
  }

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);
})();
